/**
 * Trade Builder — the only part of this workspace that actually mutates
 * state: strikes, expiry (Current Weekly / Next Weekly), quantity,
 * order type, hedge, add/remove legs, and "Convert Strategy" (rebuilds the
 * whole leg list from a different shape via `synthesize`). Every change
 * here flows back up to `StrategyAdjustmentPage`, which debounces a
 * recalculate/validate/preview/confirm refresh — this component itself
 * makes no API calls.
 */
import { useId } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ShieldIcon from "@mui/icons-material/Shield";
import type { AdjustableStrategyType, OptionLeg, OptionType, OrderSide, OrderType } from "@/types";
import { ADJUSTABLE_STRATEGY_TYPES, STRATEGY_TYPE_LABELS, fmtNum } from "./constants";

function newLegId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `leg-${Date.now()}-${Math.random()}`;
}

export interface TradeBuilderCardProps {
  legs: OptionLeg[];
  onChangeLegs: (legs: OptionLeg[]) => void;
  nearExpiryDate: string;
  farExpiryDate: string;
  defaultLotSize: number;
  strategyType: AdjustableStrategyType | null;
  onConvertStrategy: (type: AdjustableStrategyType) => void;
  convertBusy: boolean;
  /** Live spot from the recommendation — always shown, even while a
   * what-if `underlyingPriceOverride` is active, so the trader can see
   * both numbers at once. */
  liveUnderlyingPrice: number | null;
  underlyingPriceOverride: number | null;
  onChangeUnderlyingPriceOverride: (value: number | null) => void;
}

export function TradeBuilderCard({
  legs,
  onChangeLegs,
  nearExpiryDate,
  farExpiryDate,
  defaultLotSize,
  strategyType,
  onConvertStrategy,
  convertBusy,
  liveUnderlyingPrice,
  underlyingPriceOverride,
  onChangeUnderlyingPriceOverride,
}: TradeBuilderCardProps) {
  const titleId = useId();

  function updateLeg(legId: string, patch: Partial<OptionLeg>) {
    onChangeLegs(legs.map((leg) => (leg.leg_id === legId ? { ...leg, ...patch } : leg)));
  }

  function removeLeg(legId: string) {
    onChangeLegs(legs.filter((leg) => leg.leg_id !== legId));
  }

  function addLeg() {
    onChangeLegs([
      ...legs,
      {
        leg_id: newLegId(),
        option_type: "CE",
        side: "SELL",
        strike: 0,
        expiry_date: nearExpiryDate,
        expiry_bucket: "NEAR",
        quantity: 1,
        lot_size: defaultLotSize,
        order_type: "MARKET",
        limit_price: null,
        premium: null,
        iv_override_pct: null,
        is_hedge: false,
      },
    ]);
  }

  function handleExpiryChange(legId: string, bucket: "NEAR" | "FAR") {
    updateLeg(legId, {
      expiry_bucket: bucket,
      expiry_date: bucket === "NEAR" ? nearExpiryDate : farExpiryDate,
    });
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} justifyContent="space-between" flexWrap="wrap">
          <Typography id={titleId} variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
            Trade Builder
          </Typography>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id={`${titleId}-convert-label`}>Convert Strategy</InputLabel>
            <Select
              labelId={`${titleId}-convert-label`}
              label="Convert Strategy"
              value={strategyType ?? ""}
              disabled={convertBusy}
              onChange={(e) => onConvertStrategy(e.target.value as AdjustableStrategyType)}
            >
              {ADJUSTABLE_STRATEGY_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {STRATEGY_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ mb: 1.5 }} flexWrap="wrap">
          <TextField
            label="Underlying Price (what-if)"
            size="small"
            type="number"
            value={underlyingPriceOverride ?? ""}
            placeholder={liveUnderlyingPrice != null ? String(liveUnderlyingPrice) : "—"}
            onChange={(e) => onChangeUnderlyingPriceOverride(e.target.value === "" ? null : Number(e.target.value))}
            sx={{ width: 220 }}
            helperText={
              liveUnderlyingPrice != null
                ? `Live: ${liveUnderlyingPrice.toLocaleString("en-IN")}${underlyingPriceOverride != null ? " (overridden below)" : ""}`
                : "Live spot unavailable"
            }
          />
          {underlyingPriceOverride != null && (
            <Button size="small" onClick={() => onChangeUnderlyingPriceOverride(null)} sx={{ mb: 2 }}>
              Reset to live spot
            </Button>
          )}
        </Stack>

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Converting strategy shape rebuilds every leg below from scratch. Editing a field only changes
          that leg. Leave IV blank to use TekInvest's own observed/estimated volatility.
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Side</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Strike</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Expiry</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Qty (lots)
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Order Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Limit Price
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Premium
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  IV % (blank = auto)
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Hedge
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Remove
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {legs.map((leg) => (
                <TableRow key={leg.leg_id} hover>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={leg.option_type}
                      onChange={(e) => updateLeg(leg.leg_id, { option_type: e.target.value as OptionType })}
                    >
                      <MenuItem value="CE">CE</MenuItem>
                      <MenuItem value="PE">PE</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={leg.side}
                      onChange={(e) => updateLeg(leg.leg_id, { side: e.target.value as OrderSide })}
                    >
                      <MenuItem value="BUY">Buy</MenuItem>
                      <MenuItem value="SELL">Sell</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell sx={{ width: 96 }}>
                    {/* minWidth on the field itself (not just the TableCell) so a
                       5-digit strike like 23450 can never get compressed below
                       what it needs under the table's browser auto-layout —
                       without this, wider columns (e.g. the Expiry dropdown)
                       could squeeze this input down to where the native number
                       input showed only its leftmost ~3 digits. */}
                    <TextField
                      size="small"
                      variant="standard"
                      type="number"
                      inputProps={{ style: { textAlign: "right" } }}
                      sx={{ minWidth: 80 }}
                      value={leg.strike}
                      onChange={(e) => updateLeg(leg.leg_id, { strike: Number(e.target.value) })}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={leg.expiry_bucket ?? "NEAR"}
                      onChange={(e) => handleExpiryChange(leg.leg_id, e.target.value as "NEAR" | "FAR")}
                    >
                      <MenuItem value="NEAR">Current Weekly ({nearExpiryDate})</MenuItem>
                      <MenuItem value="FAR">Next Weekly ({farExpiryDate})</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 90 }}>
                    <TextField
                      size="small"
                      variant="standard"
                      type="number"
                      inputProps={{ min: 1, style: { textAlign: "right" } }}
                      value={leg.quantity}
                      onChange={(e) => updateLeg(leg.leg_id, { quantity: Math.max(1, Number(e.target.value)) })}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={leg.order_type}
                      onChange={(e) => updateLeg(leg.leg_id, { order_type: e.target.value as OrderType })}
                    >
                      <MenuItem value="MARKET">Market</MenuItem>
                      <MenuItem value="LIMIT">Limit</MenuItem>
                      <MenuItem value="SL_LIMIT">SL Limit</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>
                    {leg.order_type === "MARKET" ? (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      <TextField
                        size="small"
                        variant="standard"
                        type="number"
                        inputProps={{ style: { textAlign: "right" } }}
                        value={leg.limit_price ?? ""}
                        onChange={(e) =>
                          updateLeg(leg.leg_id, { limit_price: e.target.value === "" ? null : Number(e.target.value) })
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={leg.premium != null ? "Last observed premium" : "No observed premium — theoretically priced"}>
                      <span>{fmtNum(leg.premium, 2)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>
                    <TextField
                      size="small"
                      variant="standard"
                      type="number"
                      placeholder="Auto"
                      inputProps={{ min: 0, style: { textAlign: "right" } }}
                      value={leg.iv_override_pct ?? ""}
                      onChange={(e) =>
                        updateLeg(leg.leg_id, {
                          iv_override_pct: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    {leg.is_hedge ? (
                      <Chip
                        size="small"
                        icon={<ShieldIcon fontSize="small" />}
                        label="Hedge"
                        onClick={() => updateLeg(leg.leg_id, { is_hedge: false })}
                        color="info"
                        variant="outlined"
                      />
                    ) : (
                      <Chip size="small" label="Mark as hedge" variant="outlined" onClick={() => updateLeg(leg.leg_id, { is_hedge: true })} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Remove leg">
                      <IconButton size="small" onClick={() => removeLeg(leg.leg_id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {legs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                      No legs — add one below or pick a shape from Convert Strategy.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 1.5 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={addLeg}>
            Add Leg
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default TradeBuilderCard;
